// src/encounter/lib/to-rail-social.ts
// Derive the left-rail social cards (Occupation / Education, PROD-1259 #5) and the inline
// Active-Medications list (#7).
//
// SOURCES DIFFER (PROD-1270):
//  - Occupation / Education come from the PATIENT detail (GET /api/Patients/{id}) — they are
//    dedicated entities (patientOccupation / patientEducation), NOT social-history rows. Flutter
//    binds occupation_info_widget / education_info_widget to patient.patientOccupation /
//    patient.patientEducation. (The earlier `summary.socialHistory` regex never matched — those
//    rows never carry occupation/education — so the cards stayed empty. That was the bug.)
//  - Active Medications come from the aggregate PatientSummary (medication[]), named by
//    clinicalDisplayName: code.shortDisplay (Scrips.Patient #1224) when sent, else code.display.
import type { PatientSummary } from '@/types/patient-summary-api';
import type { PatientDetail, PatientOccupation, PatientEducation } from '@/types/patient-detail-api';
import type { RailSocialEntry } from '../patient-summary-rail';
import type { ActiveMedicationItem } from '../encounter-workflow';
import { clinicalDisplayName } from '@/lib/clinical-display-name';
import { medicationDoseLine } from './medication-dose-line';

function notDeleted<T extends { isDeleted?: boolean | null }>(items: T[] | null | undefined): T[] {
  return (items ?? []).filter((i) => !i.isDeleted);
}

function text(s: string | null | undefined): string {
  return (s ?? '').trim();
}

/** Occupation card rows from the patient's PatientOccupation entity (PROD-1270). Faithfully mirrors
 *  Flutter occupation_info_widget._buildOccupationDetails (occupation_info_widget.dart:96-132):
 *   - row 1: "{jobRole} at {employment}" / "Employment" when both present; else employment alone
 *     ("Employment") or jobRole alone ("Job role");
 *   - row 2 (in ADDITION): occupationalInformation / "Job info" when present.
 *  Returns [] when the entity is absent or all fields are blank (Flutter shows "No occupation"). */
export function toRailOccupation(detail?: PatientDetail): RailSocialEntry[] {
  const o: PatientOccupation | null | undefined = detail?.patientOccupation;
  if (!o) return [];
  const job = text(o.jobRole);
  const emp = text(o.employment);
  const info = text(o.occupationalInformation);
  const rows: RailSocialEntry[] = [];
  if (job && emp) rows.push({ label: `${job} at ${emp}`, sublabel: 'Employment' });
  else if (emp) rows.push({ label: emp, sublabel: 'Employment' });
  else if (job) rows.push({ label: job, sublabel: 'Job role' });
  if (info) rows.push({ label: info, sublabel: 'Job info' });
  return rows;
}

/** Education card rows from the patient's PatientEducation entity (PROD-1270). Mirrors Flutter
 *  education_info_widget (education_info_widget.dart:77-101) + PatientEducation.isValidData
 *  (patient_education.dart:52-57): education is shown ONLY when educationCode or educationDisplay is
 *  present (else "No education added"). When valid: educationDisplay / "Education level", THEN (in
 *  addition) educationInformation / "Education info". Returns [] when invalid/empty. */
export function toRailEducation(detail?: PatientDetail): RailSocialEntry[] {
  const e: PatientEducation | null | undefined = detail?.patientEducation;
  if (!e) return [];
  const code = text(e.educationCode);
  const disp = text(e.educationDisplay);
  const info = text(e.educationInformation);
  if (!code && !disp) return []; // Flutter isValidData: invalid unless code or display present
  const rows: RailSocialEntry[] = [];
  if (disp) rows.push({ label: disp, sublabel: 'Education level' });
  if (info) rows.push({ label: info, sublabel: 'Education info' });
  return rows;
}

export function toActiveMedications(summary?: PatientSummary): ActiveMedicationItem[] {
  return notDeleted(summary?.medication).map((m, i) => ({
    id: m.identifier ?? `med-${i}`,
    name: clinicalDisplayName(m.code).name || 'Medication',
    detail: medicationDoseLine(m),
  }));
}
